import React, { useEffect, useState } from "react";
import "./App.css";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./Authentication/Register";
import Login from "./Authentication/Login";
import ForgotPassword from "./Authentication/ForgotPassword";
import "./Home/Homepage";
import RenderOrder from "./Home/RenderOrder";
import PublicDashboard from "./Home/PublicDashboard";
import BusinessAssistant from "./MantisBusiness/Sections/Business/BusinessAssistant";
import "./MantisBusiness/Sections/Documents/Archive";
import BusinessExtraction from "./MantisBusiness/Sections/Business/BusinessExtraction";
import CierresOficinas from "./MantisBusiness/Sections/Cierres/CierresOficinas";
import CierresAlertas from "./MantisBusiness/Sections/Cierres/CierresAlertas";
import PanelCierresInicio from "./MantisBusiness/Sections/Cierres/PanelCierresInicio";
import CierresChat from "./MantisBusiness/Sections/Cierres/CierresChat";
import CierresCalendario from "./MantisBusiness/Sections/Cierres/CierresCalendario";
import CierresReportes from "./MantisBusiness/Sections/Cierres/CierresReportes";
import CierresAppShell from "./MantisBusiness/Sections/Cierres/CierresAppShell";
import "./MantisBusiness/Sections/User/UsersSection";
import BusinessIntegrations from "./MantisBusiness/Sections/Business/BusinessIntegrations";
import BusinessDashboard from "./MantisBusiness/Sections/Business/BusinessDashboard";
import BusinessUsers from "./MantisBusiness/Sections/Business/BusinessUsers";
import BusinessArchive from "./MantisBusiness/Sections/Business/BusinessArchive";
import Notifications from "./MantisBusiness/Sections/User/Notifications";
import {
  userLogin,
  userLogout,
  setUser,
  setIsAdmin,
  setIsMentor,
  setIsSherpa,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "./Redux/modules/auth";
import { connect } from "react-redux";
import sandyLogo from "./Assets/img/sandyIsotype.png";
import { isSandy } from "./config";
import mantisLogo from "./Assets/img/mantisCircle.png";
import Cookies from "js-cookie";
import { verifyToken as verifyBusinessToken } from "./ApiCalls/authentication";
import "./App.scss";
import EmailValidation from "./Extras/EmailValidation";
import "./Navbar/Navbar";
import MantisEmpresasHome from "./Extras/MantisEmpresasHome";
import BusinessLogin from "./Authentication/BusinessLogin";
import BusinessBackoffice from "./MantisBusiness/Sections/Business/BusinessBackoffice";
import BusinessSignup from "./Authentication/BusinessSignup";
import ForgotPasswordBusiness from "./Authentication/ForgotPasswordBusiness";
import DocumentSection from "./MantisBusiness/Sections/Documents/DocumentSection";
import { Helmet } from "react-helmet";
import PreventBackNavigation from "./Authentication/PreventBackNavigation";
import PublicForm from "./MantisBusiness/Sections/Forms/PublicForm";
import ProtectedRoute from "./components/ProtectedRoute";
import DataTreatmentPolicies from "./Extras/DataTreatmentPolicies";
import TermsAndConditions from "./Extras/TermsAndConditions";
import { isCierresPath } from "./utils/cierresRoutes";
import { loginPathFor } from "./utils/authRedirect";
import { checkSession } from "./utils/sessionCheck";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    userLogout: () => dispatch(userLogout()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setIsAdmin: (isAdmin) => dispatch(setIsAdmin(isAdmin)),
    setIsSherpa: (isSherpa) => dispatch(setIsSherpa(isSherpa)),
    setIsMentor: (isMentor) => dispatch(setIsMentor(isMentor)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
    authenticated: state.auth.authenticated,
    isBusiness: state.auth.isBusiness,
    mantisBusiness: state.auth.mantisBusiness,
  };
};

function App(props) {
  const navigate = useNavigate();
  useState(false);
  const [isSessionVerifying, setIsSessionVerifying] = useState(true);
  const location = useLocation();
  const excludedPaths = [
    "/",
    "/login",
    "/businessRegister",
    "/forgotPassword",
    "/forgotPasswordBusiness",
  ];
  const shouldShowNotifications = !excludedPaths.some(
    (path) => location.pathname in excludedPaths
  );

  useEffect(() => {
    function handleSessionExpiration() {
      // Clear local state only. Do not invoke server-side logout on verify failure,
      // which would destroy the active session for other open tabs.
      props.userLogout();
      navigate(loginPathFor(location), { replace: true });
    }

    // L-01 (MA-06): the JWT lives in an httpOnly cookie now, so there's nothing to
    // read from storage. The non-httpOnly businessCsrfToken cookie (set by the backend
    // at login/verifyBusinessToken) is the only readable signal a session might exist —
    // gate on that instead, same as before, just sourced from the cookie instead of the
    // token. It's businessCsrfToken, not csrfToken — that one is mantis-front/Admin's
    // own cookie pair, kept separate so the two apps' sessions can coexist in the same
    // browser instead of one login overwriting the other's cookie.
    async function verifySession() {
      console.log('🔐 App: Starting session verification...');

      // `checkSession` never rejects, and the `finally` keeps this flag settling even if a
      // dispatch below throws: ProtectedRoute waits on it, so a check that never settled left
      // every private route on "Verificando sesión..." for good.
      try {
        const result = await checkSession({
          hasSessionCookie: Boolean(Cookies.get("businessCsrfToken")),
          verify: verifyBusinessToken,
        });

        if (result.status === "valid") {
          console.log('🔐 App: Token verification successful, updating state...');
          const json = result.session;
          props.setUser({ user: json.user });
          props.setUserId({ userId: json.userId });
          props.setIsAdmin({ isAdmin: json.isAdmin });
          props.setIsMentor({ isMentor: json.isMentor });
          props.setIsSherpa({ isSherpa: json.isSherpa });
          props.setIsBusiness({ isBusiness: json.isBusiness });
          props.setMantisBusiness({ mantisBusiness: json.mantisBusiness });
          props.userLogin();
        } else if (result.status === "invalid") {
          console.log('🔐 App: Token verification failed, logging out...');
          handleSessionExpiration();
        } else if (result.status === "unreachable") {
          // Says nothing about the session, so nothing is cleared: the person is signed out
          // in this tab only, and ProtectedRoute sends them to the login with the way back.
          console.error('🔐 App: Session verification could not reach the server', result.error);
        } else {
          console.log('🔐 App: No session cookie found');
        }
      } finally {
        console.log('🔐 App: Session verification complete');
        setIsSessionVerifying(false);
      }
    }

    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>{isSandy ? "Sandy" : "Mantis"}</title>
        <link
          rel="icon"
          type="image/png"
          href={isSandy ? sandyLogo : mantisLogo}
        />
      </Helmet>
      <div className="home-component">
        {/* CierresAppShell is the one shell with a top bar of its own, and it puts the
            profile initials in the top right. This bell is `position: fixed; top: 20px;
            right: 30px; z-index: 1000`, so on those routes it painted over the avatar and
            swallowed its clicks — hence the path check. Every other shell has no top bar,
            so the bell keeps its place there. */}
        {shouldShowNotifications &&
          props.authenticated &&
          !isCierresPath(location.pathname) && <Notifications />}
        <PreventBackNavigation
          isAuthenticated={props.authenticated && props.isBusiness}
        />

        <div className="app-content">
          <Routes>
            {/* <Route path="/" exact element={<Homepage />} /> */}
            {/* <Route path="/testBelvoData" exact element={<BelvoTestData />} />
            <Route path="/signup" exact element={<Register />} /> */}
            <Route path="/ipr-status" element={<RenderOrder />} />
            <Route path="/public-dashboard" element={<PublicDashboard />} />
            <Route path="/login" exact element={<Login />} />
            <Route path="/" exact element={<BusinessLogin />} />
            <Route
              path="/businessRegister"
              exact
              element={<BusinessSignup />}
            />
            <Route
              path="/forgotPassword/:token"
              exact
              element={<ForgotPassword />}
            />
            <Route
              path="/forgotPasswordBusiness/:token"
              exact
              element={<ForgotPasswordBusiness />}
            />
            {/* <Route
              path="/widget/yoint/result/:userId"
              exact
              element={<YointResponse />}
            /> */}
            <Route path="/validateEmail" exact element={<EmailValidation />} />

            <Route
              path="/mantisEmpresas"
              exact
              element={<MantisEmpresasHome />}
            />

            <Route
              path="form/:shareId/:submissionId?"
              exact
              element={<PublicForm />}
            />

            <Route
              path="/data-treatment-policies"
              exact
              element={<DataTreatmentPolicies />}
            />

            <Route
              path="/terms-and-conditions"
              exact
              element={<TermsAndConditions />}
            />

            {/* Every signed-in route sits under this guard, and is declared whether or not
                there is a session yet: ProtectedRoute waits for the session check, then
                renders the route or sends the person to the login with the way back in
                `?redirect=`. Declaring these only once `authenticated` was true left a deep
                URL with no matching route on a fresh load — a blank page. */}
            <Route
              element={<ProtectedRoute isSessionVerifying={isSessionVerifying} />}
            >
              <Route
                path="/business/backoffice/control-panel-p2o/:iprId?"
                element={<BusinessBackoffice />}
              />
              <Route
                path="/business/backoffice"
                element={<BusinessBackoffice />}
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route
                  path="/business/backoffice:activeTab"
                  element={<BusinessBackoffice />}
                >
                  <Route
                    path="dashboard/:idDashboard?"
                    element={<BusinessDashboard />}
                  />
                  <Route path="assistant" element={<BusinessAssistant />} />
                  <Route path="archive" element={<BusinessArchive />} />
                  <Route
                    path="control-panel/:id?"
                    element={<BusinessExtraction />}
                  />
                  <Route path="users" element={<BusinessUsers />} />
                  <Route
                    path="integrations"
                    element={<BusinessIntegrations />}
                  />
                  <Route path="inbox" element={<DocumentSection />} />
                </Route>
              </Route>
              <Route
                path="/business/backoffice/outlets"
                element={<BusinessBackoffice />}
              />

              {/* The Cierres redesign's own shell (CierresAppShell) — top-level routes, not
                  nested under BusinessBackoffice, so the rest of the app's chrome is untouched
                  (see CierresAppShell.jsx's header for why), and gated on its own condition
                  on top of the sign-in guard: `clientType === "CONCILIACION"` is the
                  Brinks/cierres tenant signal (same one BusinessBackoffice.jsx's own redirect
                  and sidebar link use) — every other client type never reaches these routes,
                  even by typing the URL directly, which `authenticated && isBusiness` alone
                  would have allowed. It is sent to its own backoffice instead. */}
              <Route
                element={
                  <ProtectedRoute
                    isSessionVerifying={isSessionVerifying}
                    requireClientType="CONCILIACION"
                  />
                }
              >
                <Route
                  path="/business/backoffice/panel-cierres"
                  element={
                    <CierresAppShell>
                      <PanelCierresInicio />
                    </CierresAppShell>
                  }
                />
                <Route
                  path="/business/backoffice/oficinas"
                  element={
                    <CierresAppShell>
                      <CierresOficinas />
                    </CierresAppShell>
                  }
                />
                <Route
                  path="/business/backoffice/alertas"
                  element={
                    <CierresAppShell>
                      <CierresAlertas />
                    </CierresAppShell>
                  }
                />
                <Route
                  path="/business/backoffice/chat"
                  element={
                    <CierresAppShell>
                      <CierresChat />
                    </CierresAppShell>
                  }
                />
                <Route
                  path="/business/backoffice/calendario"
                  element={
                    <CierresAppShell>
                      <CierresCalendario />
                    </CierresAppShell>
                  }
                />
                <Route
                  path="/business/backoffice/reportes"
                  element={
                    <CierresAppShell>
                      <CierresReportes />
                    </CierresAppShell>
                  }
                />
                {/* Real BusinessUsers screen, just framed by the fixed Cierres shell instead of
                    jumping back to BusinessBackoffice's old sidebar — the component itself keeps
                    its existing Poppins styling; a full visual port is a separate task. */}
                <Route
                  path="/business/backoffice/usuarios"
                  element={
                    <CierresAppShell>
                      <BusinessUsers />
                    </CierresAppShell>
                  }
                />
              </Route>
            </Route>

            {/* Any other URL — the deeper backoffice ones included, since
                `/business/backoffice:activeTab` takes a single segment — still needs a
                session: without one it goes to the login and comes back here. With one it
                renders what it always did, rather than a redirect that BusinessDashboard's
                own `navigate("/business/backoffice/dashboard/1")` would turn into a loop. */}
            <Route
              path="*"
              element={<ProtectedRoute isSessionVerifying={isSessionVerifying} />}
            />
          </Routes>
        </div>
        {/* </BrowserRouter> */}
      </div>
    </>
  );
}

export default connect(select, mapDispatchToProps)(App);
